import {TextField} from '@mui/material';
import {DateRangePicker, LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterDayjs} from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Tooltip as ReactTooltip} from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import SessionFilter from '../Components/Global_Components/common/SessionFilter';
import BasicTable from '../Components/Global_Components/reacttable/table';
import {fetchPatientBill, getArchiveData} from '../Redux/Thunk/Actions';
import '../styles/Patients.scss';
import {downloadExcel, printPDF} from '../utils/printUtils';

dayjs.extend(localizedFormat);

const BillReportDetails = ({loaderchange, popupalert, popuptext}) => {
  const dispatch = useDispatch();
  const userdata = useSelector(state => state.counter.userdata);
  const sideactive = useSelector(state => state.counter.sidebarnav);

  // State management
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({pageIndex: 1, pageSize: 10});
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [showArchive, setShowArchive] = useState(false);
  const [totalBill, setTotalBill] = useState(0);
  const [datacount, setdatacount] = useState(10);
  const [session, setSession] = useState('');
  const [isReportLoading, setIsReportLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  // Derived values
  const getFormattedDates = range => {
    const s = range && range[0] && dayjs(range[0]).isValid() ? dayjs(range[0]).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    const e = range && range[1] && dayjs(range[1]).isValid() ? dayjs(range[1]).format('YYYY-MM-DD') : s;
    return {startDate: s, endDate: e};
  };

  const {startDate, endDate} = getFormattedDates(dateRange);
  const isAdmin = userdata.role === 'Admin';

  const appliedFiltersRef = useRef({
    search: '',
    from_date: dayjs().format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
    session: ''
  });

  const headers = {
    serialNo: 'S.NO',
    date: 'DATE',
    patientName: 'PATIENTS  NAME',
    billAmount: 'BILL AMOUNT',
    cashCollected: 'CASH COLLECTED',
    balance: 'BALANCE',
    card: 'CARD',
    upi: 'UPI',
    remarks: 'REMARKS'
  };

  const subHeaders = {
    billTotal: 'BILL',
    cashTotal: 'CASH',
    totalBalance: 'BALANCE',
    cardTotal: 'CARD',
    upiTotal: 'UPI'
  };

  const fetchData = async (page = 1, pageSize = 10, customFilters = null, isArchive = false) => {
    const reqId = ++activeRequestIdRef.current;
    setIsReportLoading(true);
    loaderchange(true);

    const filters = customFilters || appliedFiltersRef.current;
    const payload = {
      page: page,
      page_size: pageSize,
      search: filters.search,
      from_date: filters.from_date,
      to_date: filters.to_date,
      range: true,
      session: filters.session
    };

    try {
      const action = isArchive ? getArchiveData : fetchPatientBill;
      const response = await dispatch(action(payload));

      if (reqId !== activeRequestIdRef.current) return;

      if (response?.payload?.status?.code === 200) {
        const {data, total_count, total_bill_price} = response.payload;
        setdatacount(total_count || 0);
        const records = data?.data || data || [];
        setTableData(Array.isArray(records) ? records : []);
        setTotalBill(total_bill_price || 0);

        if ((Array.isArray(records) ? records : []).length === 0) {
          popupalert(true);
          popuptext('No Data Available');
          setTimeout(() => popupalert(false), 2000);
        }
      } else {
        setTableData([]);
        setdatacount(0);
        setTotalBill(0);
        popupalert(true);
        popuptext('No Data Available');
        setTimeout(() => popupalert(false), 2000);
      }
    } catch (error) {
      console.error('Error fetching billing report:', error);
      if (reqId === activeRequestIdRef.current) {
        setTableData([]);
        setdatacount(0);
        setTotalBill(0);
        popupalert(true);
        popuptext('Error fetching report');
        setTimeout(() => popupalert(false), 2000);
      }
    } finally {
      if (reqId === activeRequestIdRef.current) {
        setIsReportLoading(false);
        loaderchange(false);
      }
    }
  };

  const handleSearch = () => {
    if (isReportLoading) return;
    const {startDate: fromDate, endDate: toDate} = getFormattedDates(dateRange);
    const newFilters = {
      search: searchQuery,
      from_date: fromDate,
      to_date: toDate,
      session: session
    };
    appliedFiltersRef.current = newFilters;
    setPagination(prev => ({...prev, pageIndex: 1}));
    fetchData(1, pagination.pageSize, newFilters, showArchive);
  };

  useEffect(() => {
    handleSearch();
  }, [showArchive]);

  const [billTotal, cashTotal, cardTotal, upiTotal, totalBalance] = useMemo(() => {
    let bill = 0,
      cash = 0,
      card = 0,
      upi = 0,
      balance = 0;
    tableData.forEach(item => {
      bill += Math.round(Number(item.bill_price) || 0);
      cash += Math.round(Number(item.amount_one) || 0);
      card += Math.round(Number(item.amount_two) || 0);
      upi += Math.round(Number(item.amount_three) || 0);
    });
    balance = Math.round(cash ?? 0) + Math.round(card ?? 0) + Math.round(upi ?? 0) - Math.round(bill ?? 0);
    return [bill, cash, card, upi, balance];
  }, [tableData]);

  const [currentPageBillTotal, currentPageCashTotal, currentPageCardTotal, currentPageUpiTotal, currentPageBalance] = useMemo(() => {
    let bill = 0,
      cash = 0,
      card = 0,
      upi = 0,
      balance = 0;
    tableData.forEach(item => {
      bill += Math.round(Number(item.bill_price) || 0);
      cash += Math.round(Number(item.amount_one) || 0);
      card += Math.round(Number(item.amount_two) || 0);
      upi += Math.round(Number(item.amount_three) || 0);
    });
    balance = Math.round(cash ?? 0) + Math.round(card ?? 0) + Math.round(upi ?? 0) - Math.round(bill ?? 0);
    return [bill, cash, card, upi, balance];
  }, [tableData]);

  const handlePageSizeChange = useCallback(pageSize => {
    setPagination(prev => {
      if (prev.pageSize === pageSize) return prev;
      const updated = {...prev, pageSize, pageIndex: 1};
      fetchData(1, pageSize, appliedFiltersRef.current, showArchive);
      return updated;
    });
  }, [showArchive]);

  const handlePageIndexChange = useCallback(pageIndex => {
    setPagination(prev => {
      if (prev.pageIndex === pageIndex) return prev;
      const updated = {...prev, pageIndex};
      fetchData(pageIndex, prev.pageSize, appliedFiltersRef.current, showArchive);
      return updated;
    });
  }, [showArchive]);

  const handleDateChange = newDates => {
    setDateRange(newDates);
  };

  const handleSessionChange = useCallback(newSession => {
    setSession(newSession);
  }, []);

  const handleHTMLData = data => {
    if (!data) return '--';
    const regex = /<p[^>]*class="[^"]*remarks-column[^"]*"[^>]*>(.*?)<\/p>/gi;
    const matches = [];
    let match;
    while ((match = regex.exec(data)) !== null) {
      matches.push(match[1]);
    }
    return matches.length > 0 ? matches.join(', ') : '--';
  };

  const formattedData = tableData.map((item, index) => ({
    serialNo: index + 1,
    date: item.created_at ? dayjs(item.created_at).format('DD-MM-YYYY') : '--',
    patientName: `${item.patient_id?.titles || ''} ${item.patient_id?.first_name || ''} ${item.patient_id?.last_name || ''}`.trim(),
    billAmount: Math.round(item.bill_price) || 0,
    cashCollected: Math.round(item.amount_one) || 0,
    balance: Math.round(Number(item.amount_one) || 0) + Math.round(Number(item.amount_two) || 0) + Math.round(Number(item.amount_three) || 0) - (Math.round(Number(item.bill_price) || 0)),
    card: Math.round(item.amount_two) || 0,
    upi: Math.round(item.amount_three) || 0,
    remarks: handleHTMLData(item.report_template)
  }));

  const handlePrintPdf = () => {
    const subRow = [billTotal, cashTotal, totalBalance, cardTotal, upiTotal];
    printPDF({title: 'Billing Report', headers, data: formattedData, subHeaders, subRow});
  };

  const handleDownload = () => {
    const subRow = ['', '', '', billTotal, cashTotal, totalBalance, cardTotal, upiTotal, ''];
    downloadExcel({title: 'Billing Report', headers, data: formattedData, subRow});
  };

  const tableColumns = useMemo(
    () => [
      {
        Header: 'SI No.',
        accessor: '',
        Cell: row => {
          return (
            <>
              <span>
                <div style={{marginTop: '3px'}} data-tooltip-id='my-tooltip' data-tooltip-content={String(1 + Number(row.row.id))}>
                  {1 + Number(row.row.id)}.
                </div>
              </span>
            </>
          );
        }
      },
      {
        Header: 'Date',
        accessor: 'created_at',
        Cell: ({value}) => <TooltipContent>{value ? dayjs(value).format('DD-MM-YYYY') : '--'}</TooltipContent>
      },
      {
        Header: 'Patient Name',
        accessor: row => `${row.patient_id?.titles || ''} ${row.patient_id?.first_name || ''} ${row.patient_id?.last_name || ''}`.trim(),
        Cell: ({row}) => (
          <TooltipContent>
            {row.original.patient_id?.titles}.{row.original.patient_id?.first_name} {row.original.patient_id?.last_name}
          </TooltipContent>
        ),
        Footer: () => <span>TOTAL</span>
      },
      {
        Header: 'Bill Amount',
        accessor: 'bill_price',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || 0}</TooltipContent>,
        Footer: () => <span>{currentPageBillTotal && Math.round(currentPageBillTotal)}</span>
      },
      {
        Header: 'Cash Collected',
        accessor: 'amount_one',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || 0}</TooltipContent>,
        Footer: () => <span>{currentPageCashTotal && Math.round(currentPageCashTotal)}</span>
      },
      {
        Header: 'Balance',
        accessor: 'balance',
        Cell: ({row}) => {
          const balance =
            Math.round(Number(row.original.amount_one) || 0) +
            Math.round(Number(row.original.amount_two) || 0) +
            Math.round(Number(row.original.amount_three) || 0) -
            Math.round(Number(row.original.bill_price) || 0);
          return <TooltipContent>{balance}</TooltipContent>;
        },
        Footer: () => <span>{currentPageBalance && Math.round(currentPageBalance)}</span>
      },
      {
        Header: 'Card',
        accessor: 'amount_two',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || 0}</TooltipContent>,
        Footer: () => <span>{currentPageCardTotal && Math.round(currentPageCardTotal)}</span>
      },
      {
        Header: 'UPI',
        accessor: 'amount_three',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || 0}</TooltipContent>,
        Footer: () => <span>{currentPageUpiTotal && Math.round(currentPageUpiTotal)}</span>
      },
      {
        Header: 'Remarks',
        accessor: 'report_template',
        Cell: ({value}) => {
          if (!value) return <TooltipContent>--</TooltipContent>;
          const regex = /<p[^>]*class="[^"]*remarks-column[^"]*"[^>]*>(.*?)<\/p>/gi;
          const remarks = [];
          let match;
          while ((match = regex.exec(value)) !== null) {
            remarks.push(match[1]);
          }
          return (
            <div
              data-tooltip-id='table-tooltip'
              data-tooltip-html={remarks.map(item => `<div>${item}</div>`).join('')}
              style={{
                maxWidth: '200px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
              {remarks.length > 0 ? remarks.join(', ') : '--'}
            </div>
          );
        }
      }
    ],
    [showArchive, currentPageBillTotal, currentPageCashTotal, currentPageBalance, currentPageCardTotal, currentPageUpiTotal]
  );

  return (
    <div className={`maincontainer-patient ${sideactive ? 'maincontainer-patient-sa' : ''}`}>
      <div className='patient-header'>
        <Breadcrumbs />
        <div className='add_btn' style={{display: 'flex', gap: '10px'}}>
          <button onClick={handlePrintPdf} className='btn_1' disabled={isReportLoading} style={{opacity: isReportLoading ? 0.7 : 1, cursor: isReportLoading ? 'not-allowed' : 'pointer'}}>
            Print
          </button>
          <button onClick={handleDownload} className='btn_1' disabled={isReportLoading} style={{opacity: isReportLoading ? 0.7 : 1, cursor: isReportLoading ? 'not-allowed' : 'pointer'}}>
            Download
          </button>
        </div>
      </div>

      {isAdmin && (
        <div className='billableCard'>
          <p>
            {startDate === dayjs().format('YYYY-MM-DD') && endDate === dayjs().format('YYYY-MM-DD') ? "Today's Billing Overview" : 'Billing Overview'}
            &nbsp;:&nbsp;
            <span>{totalBill ?? 0}&nbsp;Rs</span>
          </p>
        </div>
      )}

      <div className='card'>
        <div className='card-body'>
          <div className='table-controls' style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px'}}>
            <SearchInput value={searchQuery} onChange={setSearchQuery} onSearch={handleSearch} disabled={isReportLoading} />
            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
              <SessionFilter value={session} onChange={handleSessionChange} disabled={isReportLoading} />
              <DateRangeFilter value={dateRange} onChange={handleDateChange} disabled={isReportLoading} />
              <button
                type='button'
                className='btn'
                style={{
                  backgroundColor: '#1b5a90',
                  color: '#ffffff',
                  height: '40px',
                  padding: '0 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isReportLoading ? 0.7 : 1,
                  cursor: isReportLoading ? 'not-allowed' : 'pointer'
                }}
                disabled={isReportLoading}
                onClick={handleSearch}
              >
                {isReportLoading ? (
                  <>
                    <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' style={{width: '14px', height: '14px', borderWidth: '2px'}}></span>
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <i className='bi bi-search' style={{fontSize: '13px'}}></i>
                    <span>Search</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <BasicTable
            columns={tableColumns}
            data={tableData}
            pagesize={handlePageSizeChange}
            pageindex={handlePageIndexChange}
            dtpagesize={pagination.pageSize}
            dtpageindex={pagination.pageIndex}
            datacount={datacount}
            showAll={true}
            disabled={isReportLoading}
          />
        </div>
      </div>

      <ReactTooltip id='table-tooltip' arrowColor='transparent' place='bottom-start' delayShow={200} className='custom-tooltip' />
    </div>
  );
};

// Sub-components for better readability
const TooltipContent = ({children}) => (
  <div data-tooltip-id='table-tooltip' data-tooltip-content={children}>
    {children}
  </div>
);

const Breadcrumbs = () => (
  <div className='row'>
    <div className='col'>
      <h3 className='page-title'>Billing Report</h3>
      <ul className='breadcrumb'>
        <li className='breadcrumb-item'>
          <a href='/'>Dashboard</a>
        </li>
        <li className='breadcrumb-item active'>Billing Report</li>
      </ul>
    </div>
  </div>
);

const SearchInput = ({value, onChange, onSearch, disabled}) => (
  <div className='input-group rounded' style={{width: '250px', height: '40px'}}>
    <input
      type='search'
      className='form-control rounded'
      aria-label='Search'
      aria-describedby='search-addon'
      value={value}
      disabled={disabled}
      style={{
        height: '40px'
      }}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onSearch && onSearch();
        }
      }}
      placeholder='Search...'
      maxLength={100}
    />
    <span 
      className='input-group-text border-0 bg-transparent p-0' 
      id='search-addon'
      style={{cursor: disabled ? 'not-allowed' : 'pointer'}}
      onClick={() => !disabled && onSearch && onSearch()}
    >
      <i
        className='bi bi-search'
        style={{
          color: '#1b5a90',
          width: '20px',
          height: '20px',
          marginLeft: '10px'
        }}></i>
    </span>
  </div>
);

const DateRangeFilter = ({value, onChange, disabled}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DateRangePicker
      value={value}
      onChange={onChange}
      disabled={disabled}
      inputFormat='DD/MM/YYYY'
      maxDate={dayjs()}
      renderInput={(startProps, endProps) => (
        <>
          <FormattedDateField {...startProps} disabled={disabled} />
          <FormattedDateField {...endProps} disabled={disabled} />
        </>
      )}
    />
  </LocalizationProvider>
);

const FormattedDateField = props => <TextField {...props} InputLabelProps={{shrink: true}} />;

export default BillReportDetails;
